import React, { useEffect, useState } from "react";
import {
    Alert,
    Snackbar,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import {
    getInstitutionServices,
    editService,
    deleteService,
    addNewService,
} from "../../services/service";
import Table from "../../components/Table/Table";
import ConfirmationModal from "../../components/Modals/ConfirmationModal/ConfirmationModal";

export default function ManageServices() {
    const { user, token, institution } = useAuth();
    const institutionId = institution?.id;

    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editId, setEditId] = useState(null);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarContent, setSnackbarContent] = useState({
        message: "",
        type: "",
    });

    const handleSnackbar = (message, type = "success") => {
        setSnackbarContent({ message, type });
        setSnackbarOpen(true);
    };

    const handleAddService = (setRecentlyAddedId) => {
        if (editId) return;

        const tempId = "temp-" + Date.now();
        const newRow = {
            id: tempId,
            name: "",
            servicePeriod: "",
            reservationType: "queue",
            reservationLabel: "طابور",
        };

        setServices((prev) => [newRow, ...prev]);
        setEditId(tempId);
        setRecentlyAddedId(tempId);
    };

    const onSave = async (id, newData) => {
    const isNew = id.toString().startsWith("temp-");

    const typeMap = {
        "طابور": "queue",
        "موعد": "appointment",
    };

    try {
        if (isNew) {
            const requestBody = {
                name: newData.name,
                servicePeriod: Number(newData.servicePeriod),
                reservationType: typeMap[newData.reservationLabel] || "queue",
                institutionId,
                branchId: user.branchId,
            };

            const response = await addNewService(token, requestBody);
            const service = response.data;

            const formatted = {
                id: service.id,
                name: service.name,
                servicePeriod: service.servicePeriod,
                reservationType: service.reservationType,
                reservationLabel: service.reservationType === "queue" ? "طابور" : "موعد",
            };

            setServices((prev) => prev.map((s) => (s.id === id ? formatted : s)));
            handleSnackbar("تمت إضافة الخدمة بنجاح");
        } else {
            await editService(token, id, newData);
            setServices((prev) =>
                prev.map((s) => (s.id === id ? { ...s, ...newData } : s))
            );
            handleSnackbar("تم تعديل الخدمة بنجاح");
        }
    } catch (err) {
        if (isNew) {
            // Remove the row if save fails
            setServices((prev) => prev.filter((s) => s.id !== id));
        }

        const errorData = err.response?.data;
        if (errorData?.errors?.length > 0) {
            // Extract message from validation error
            const messages = errorData.errors.map((e) => e.msg).join("، ");
            handleSnackbar(messages, "error");
        } else {
            handleSnackbar(errorData || err.message, "error");
        }
    } finally {
        setEditId(null);
    }
};


    const onDelete = async () => {
        setIsLoading(true);
        setConfirmationModalOpen(false);
        try {
            await deleteService(token, serviceToDelete);
            setServices((prev) => prev.filter((s) => s.id !== serviceToDelete));
            handleSnackbar("تم حذف الخدمة بنجاح");
        } catch (err) {
            handleSnackbar(err.response?.data || err.message, "error");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchServices = async () => {
            setIsLoading(true);
            try {
                const res = await getInstitutionServices(institutionId);
                const formatted = res.data.map((s) => ({
                    ...s,
                    reservationLabel:
                        s.reservationType === "queue" ? "طابور" : "موعد",
                }));
                setServices(formatted);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };

        if (institutionId) fetchServices();
    }, [institutionId]);

    const columns = [
    { key: "name", label: "اسم الخدمة" },
    {
        key: "servicePeriod",
        label: "مدة الخدمة (دقيقة)",
        isNumber: true,
    },
    {
        key: "reservationLabel",
        label: "نوع الحجز",
        isDropdown: true,
        options: [
            { value: "طابور", label: "طابور" },
            { value: "موعد", label: "موعد" },
        ],
    },
];


    const isEditableColumn = (columnKey, row) => {
        if (columnKey === "reservationLabel") {
            return row.id?.toString().startsWith("temp-"); // editable only for new rows
        }
    return true; // all other fields remain editable
    };


    return (
        <div className="page">
            <div className="content-view">
                <h1 className="text-xl font-bold mb-4">إدارة الخدمات</h1>

                <Table
                 isEditableColumn={isEditableColumn}
                    data={services}
                    columns={columns}
                    loading={isLoading}
                    onDelete={(id) => {
                        setServiceToDelete(id);
                        setConfirmationModalOpen(true);
                    }}
                    onSave={onSave}
                    onAdd={handleAddService}
                    editId={editId}
                    setEditId={setEditId}
                    editableColumns={["name", "servicePeriod", "reservationLabel"]}
                    showDetailsColumn={false}
                    highlightConfig={{
                        reservationLabel: {
                            'طابور': "#01c8aa",
                            'موعد': "#4787fd",
                        },
                    }}
                    tagColumn="reservationLabel"
                />

                {confirmationModalOpen && (
                    <ConfirmationModal
                        message="هل أنت متأكد أنك تريد حذف هذه الخدمة؟"
                        handleConfirm={onDelete}
                        onClose={() => setConfirmationModalOpen(false)}
                    />
                )}

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarContent.type}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbarContent.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
}
